<template>
    <div class="flex items-center space-x-4">
        <div class="w-full">
            <div v-if="!isDatePickerVisible" class="w-full">
                <Menu as="div" class="relative inline-block w-full text-left">
                    <div>
                        <MenuButton
                            class="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            :disabled="disabled">
                            {{ selectedOptionLabel }}
                            <ChevronDownIcon class="-mr-1 size-5 text-gray-400" aria-hidden="true" />
                        </MenuButton>
                    </div>

                    <transition
                        enter-active-class="transition ease-out duration-100"
                        enter-from-class="transform opacity-0 scale-95"
                        enter-to-class="transform opacity-100 scale-100"
                        leave-active-class="transition ease-in duration-75"
                        leave-from-class="transform opacity-100 scale-100"
                        leave-to-class="transform opacity-0 scale-95">
                        <MenuItems
                            :style="{ zIndex: 1999 }"
                            class="absolute left-0 z-10 mt-2 w-48 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden">
                            <div class="py-1">
                                <MenuItem v-slot="{ active }" v-for="option in options" :key="option.value">
                                    <a
                                        href="#"
                                        @click="selectOption(option)"
                                        :class="[
                                            active || selectedRange === (option.key || option.value)
                                                ? 'bg-gray-100 text-cyan-950 outline-hidden'
                                                : 'text-cyan-950',
                                            'block w-full text-left px-4 py-2 text-sm'
                                        ]">
                                        {{ option.label }}
                                    </a>
                                </MenuItem>
                            </div>
                        </MenuItems>
                    </transition>
                </Menu>
            </div>

            <div v-else class="text-sm flex flex-col items-center">
                <div v-if="isRange && inputValue" class="text-xs text-gray-600 mt-1">
                    {{ formatRange(inputValue) }}
                </div>
                <div class="flex items-center gap-2">
                    <ChevronLeftIcon class="w-5 h-5 cursor-pointer hover:text-gray-800" @click="showDropdown" />
                    <VueDatePicker
                        v-model="inputValue"
                        :disabled="disabled || loading"
                        :readonly="disabled || loading"
                        :type="datePickerType"
                        :range="isRange"
                        class="w-full text-sm shadow-xs" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { ChevronLeftIcon, ChevronDownIcon } from '@heroicons/vue/24/outline';
import { getDatesBasedOnFilter } from '@/utils/utils';
import { DATE_FILTER_OPTIONS } from '@/utils/constants';

const props = defineProps({
    disabled: Boolean,
    loading: Boolean,
    placeholder: String,
    hideOptions: { type: Array, default: () => [] },
    selected: { type: String, default: '' }
});
const emit = defineEmits(['input']);

const inputValue = ref('');
const selectedRange = ref(props.selected || 'last24h');
const datePickerType = ref('date');
const isRange = ref(false);
const isDatePickerVisible = ref(false);
const options = ref(DATE_FILTER_OPTIONS);

const selectedOptionLabel = computed(() => {
    const found = options.value.find((option) => option.value === selectedRange.value);
    return found ? found.label : props.loading ? 'Loading...' : props.placeholder || 'Select date filter';
});

const selectOption = (option) => {
    selectedRange.value = option.value;
    onRangeChange();
};

onMounted(() => {
    if (props.hideOptions?.length) {
        options.value = options.value.filter((option) => !props.hideOptions.includes(option.value));
    }
});

watch(
    () => inputValue.value,
    (newValue) => {
        if (selectedRange.value === 'date' || selectedRange.value === 'dateRange') {
            emit('input', {
                type: selectedRange.value === 'dateRange' ? 'range' : 'date',
                key: selectedRange.value,
                value: isRange.value ? { start: newValue?.[0], end: newValue?.[1] } : newValue
            });
        }
    }
);

watch(
    () => selectedRange.value,
    (newValue) => {
        if (newValue !== 'date' && newValue !== 'dateRange') {
            emit('input', { type: 'custom', key: newValue, value: getDatesBasedOnFilter(newValue) });
        } else {
            if (newValue === 'date') {
                inputValue.value = new Date();
            } else if (newValue === 'dateRange') {
                inputValue.value = [new Date(new Date().setDate(new Date().getDate() - 5)), new Date()];
            }
        }
    }
);

function onRangeChange() {
    isDatePickerVisible.value = selectedRange.value === 'date' || selectedRange.value === 'dateRange';
    isRange.value = selectedRange.value === 'dateRange';
}

function showDropdown() {
    isDatePickerVisible.value = false;
    selectedRange.value = '';
    inputValue.value = '';
}

function formatRange(range) {
    if (range?.[0] && range?.[1]) {
        return `${range[0].toLocaleDateString()} - ${range[1].toLocaleDateString()}`;
    }
    return '';
}
</script>
